(() => {
  const easeOutCubic = (progress) => 1 - Math.pow(1 - progress, 3);

  class DualPromoCountUp {
    constructor(element) {
      this.element = element;
      this.from = Number(element.dataset.countFrom || 0);
      this.to = Number(element.dataset.countTo || 0);
      this.duration = Number(element.dataset.countDuration || 1600);
      this.decimals = Number(element.dataset.countDecimals || 0);
      this.replay = element.dataset.countReplay === 'true';
      this.hasPlayed = false;
      this.animationFrame = null;
      this.render(this.from);
    }

    render(value) {
      this.element.textContent = Number(value).toFixed(this.decimals);
    }

    start() {
      if (this.hasPlayed && !this.replay) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.render(this.to);
        this.hasPlayed = true;
        return;
      }

      cancelAnimationFrame(this.animationFrame);
      const startedAt = performance.now();

      const tick = (now) => {
        const elapsed = now - startedAt;
        const progress = Math.min(elapsed / this.duration, 1);
        const easedProgress = easeOutCubic(progress);
        const currentValue = this.from + (this.to - this.from) * easedProgress;

        this.render(currentValue);

        if (progress < 1) {
          this.animationFrame = requestAnimationFrame(tick);
        } else {
          this.render(this.to);
          this.hasPlayed = true;
        }
      };

      this.animationFrame = requestAnimationFrame(tick);
    }

    reset() {
      if (!this.replay) return;
      cancelAnimationFrame(this.animationFrame);
      this.render(this.from);
      this.hasPlayed = false;
    }

    destroy() {
      cancelAnimationFrame(this.animationFrame);
    }
  }

  class DualPromoMarquee extends HTMLElement {
    connectedCallback() {
      this.cleanup();

      this.counters = Array.from(this.querySelectorAll('[data-count-up]')).map(
        (element) => new DualPromoCountUp(element)
      );

      this.setupCountObserver();
      this.setupMobileSlider();
    }

    setupCountObserver() {
      if (!this.counters.length) return;

      this.countObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.counters.forEach((counter) => counter.start());
            } else {
              this.counters.forEach((counter) => counter.reset());
            }
          });
        },
        { threshold: 0.3 }
      );

      this.countObserver.observe(this);
    }

    setupMobileSlider() {
      this.slider = this.querySelector('.dual-promo-marquee__cards--mobile-carousel');
      this.cards = Array.from(this.slider?.querySelectorAll('.dual-promo-card') || []);
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));

      if (!this.slider || this.cards.length < 2 || this.dots.length < 2) return;

      this.scrollFrame = null;
      this.onSliderScroll = () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => this.updateActiveDot());
      };

      this.onResize = () => {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.updateActiveDot());
      };

      this.slider.addEventListener('scroll', this.onSliderScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.dotHandlers = this.dots.map((dot, index) => {
        const handler = () => this.scrollToCard(index);
        dot.addEventListener('click', handler);
        return handler;
      });

      requestAnimationFrame(() => this.updateActiveDot());
    }

    scrollToCard(index) {
      const card = this.cards[index];
      if (!card || !this.slider) return;

      const sliderStyle = window.getComputedStyle(this.slider);
      const paddingLeft = parseFloat(sliderStyle.paddingLeft) || 0;
      const targetLeft = Math.max(0, card.offsetLeft - paddingLeft);

      this.slider.scrollTo({
        left: targetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });

      this.setActiveDot(index);
    }

    updateActiveDot() {
      if (!this.slider || !this.cards?.length) return;

      const viewportCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
      let activeIndex = 0;
      let shortestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - viewportCenter);

        if (distance < shortestDistance) {
          shortestDistance = distance;
          activeIndex = index;
        }
      });

      this.setActiveDot(activeIndex);
    }

    setActiveDot(activeIndex) {
      this.dots?.forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    cleanup() {
      this.countObserver?.disconnect();
      this.slider?.removeEventListener('scroll', this.onSliderScroll);
      window.removeEventListener('resize', this.onResize);

      this.dots?.forEach((dot, index) => {
        const handler = this.dotHandlers?.[index];
        if (handler) dot.removeEventListener('click', handler);
      });

      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.resizeFrame);
      this.counters?.forEach((counter) => counter.destroy());
    }

    disconnectedCallback() {
      this.cleanup();
    }
  }

  if (!customElements.get('dual-promo-marquee')) {
    customElements.define('dual-promo-marquee', DualPromoMarquee);
  }
})();
